import React, {Component} from "react"

import Flex from "@irati/ui/src/Flex"
import {H2, H3, Body} from "@irati/ui/src/Typography"
import {FormattedMessage} from "@irati/utils/lang"
import authentication from "@irati/auth"
import workosService from "@irati/workos/services/workos"
import Close from "@irati/ui/src/Close"
import theme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import OnboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Spacing from "@irati/ui/src/Spacing"
import {Row, Col} from "@irati/ui/src/ColumnRow"
import SliderInfo, {SlideInfo} from "@irati/ui/src/Slider/SliderInfo"

import integrationsModuleGroup1 from "@irati/ui/images/integrations-module-group1.svg"
import integrationsModuleGroup2 from "@irati/ui/images/integrations-module-group2.svg"
import bambooHr from "@irati/ui/images/integrations-module-bamboo.svg"

import InfoCard from "./InfoCard"
import Feedback from "./Feedback"
import {RelativeCard, Image, ManyMore} from "./styled"

const SLIDES_INFO: SlideInfo[] = [
  {
    titleId: "admin.dashboard.integrations.slider.one.title",
    subTitleId: "admin.dashboard.integrations.slider.one.desc"
  },
  {
    titleId: "admin.dashboard.integrations.slider.second.title",
    subTitleId: "admin.dashboard.integrations.slider.second.desc"
  },
  {
    titleId: "admin.dashboard.integrations.slider.three.title",
    subTitleId: "admin.dashboard.integrations.slider.three.desc"
  }
]

interface Props {}

interface State {
  showIntegrations: boolean,
  singleSignOnEnabled: boolean,
  directorySyncEnabled: boolean,
  showMore: boolean,
  showFeedback: boolean,
  doNotShowAgain: boolean
}

class Integrations extends Component<Props, State> {
  state: State = {
    showIntegrations: false,
    singleSignOnEnabled: false,
    directorySyncEnabled: false,
    showMore: false,
    showFeedback: false,
    doNotShowAgain: false
  }

  componentDidMount() {
    OnboardingService.get(authentication.user().uuid).then((res) => {
      const hasHiddenModule = res.userProgress.find(({step}) => {
        return step === OnboardingStep.HAS_HIDDEN_INTEGRATIONS_MODULE
      })
      if (!hasHiddenModule) {
        workosService.getOrganisation(authentication.business().uuid).then((organisation) => {
          const showIntegrations = !organisation?.connectionId || !organisation?.directoryId

          if (showIntegrations) {
            this.setState({
              showIntegrations: true,
              singleSignOnEnabled: !!organisation?.connectionId,
              directorySyncEnabled: !!organisation?.directoryId
            })
          }
        })
      }
    })
  }

  handleShowMore = () => {
    this.setState((prevState: State) => {
      if (!prevState.showMore) {
        adminDashboardTracking.clickFindOutMore()
      } else {
        adminDashboardTracking.clickReadLess()
      }
      return {showMore: !prevState.showMore}
    })
  }

  handleNotShowAgain = () => {
    this.setState({doNotShowAgain: true})
    adminDashboardTracking.clickNotShowAgain()
  }

  handleShowFeedback = () => {
    this.setState({showFeedback: true, doNotShowAgain: false})
  }

  handleOnHideFeedback = () => {
    this.setState({showFeedback: false})
    adminDashboardTracking.closeIntegrationModule()
  }

  handleClose = () => {
    this.setState({showIntegrations: false})
    adminDashboardTracking.closeIntegrationModule()
  }

  handleCloseReminder = () => {
    this.setState({showIntegrations: false})
    adminDashboardTracking.confirmIntegrationModuleReminder()
  }

  render() {
    const {
      showIntegrations,
      singleSignOnEnabled,
      directorySyncEnabled,
      showMore,
      showFeedback,
      doNotShowAgain
    } = this.state
    const buttonText = showMore ? "admin.dashboard.integrations.button.readLess" : "admin.dashboard.integrations.button.findOutMore"
    const icon = !showMore ? IconTypes.circleExpand : IconTypes.circleContract

    if (!showIntegrations) {
      return null
    }
    return (
      <RelativeCard marginBottom={2}>
        {showFeedback && <Feedback
          onHideIntegrations={this.handleClose}
          onHideFeedback={this.handleOnHideFeedback} />}
        {doNotShowAgain && <>
          <H2 marginBottom={2}>
            <FormattedMessage id="admin.dashboard.integrations.doNotShowAgain.title" />
          </H2>
          <Body marginBottom={4}>
            <FormattedMessage id="admin.dashboard.integrations.doNotShowAgain.text" />
          </Body>
          <Flex>
            <Button primary onClick={this.handleCloseReminder} marginRight={2}>
              <FormattedMessage id="admin.dashboard.integrations.doNotShowAgain.button.yes" />
            </Button>
            <Button onClick={this.handleShowFeedback}>
              <FormattedMessage id="admin.dashboard.integrations.doNotShowAgain.button.no" />
            </Button>
          </Flex>
        </>}
        {!showFeedback && !doNotShowAgain && <>
          <Close
            id="moduleCloseX"
            top={theme.spacing.two}
            right={theme.spacing.two}
            onClick={this.handleNotShowAgain} />
          <Row marginBottom={3}>
            <Col xs={4}>
              <H3 marginTop={5}>
                <FormattedMessage id="admin.dashboard.integrations.title" />
              </H3>
            </Col>
            <Col xs={8}>
              <SliderInfo slides={SLIDES_INFO} trackEvent={adminDashboardTracking.clickIntegrationSliderArrows}  />
            </Col>
          </Row>
          <Flex marginBottom={4} gap={theme.spacing.oneAndHalf}>
            <Image src={integrationsModuleGroup1} alt="integrationsModuleGroup1" />
            <Image src={integrationsModuleGroup2} alt="integrationsModuleGroup2" />
            <Image src={bambooHr} alt="bambooHr" />
            <ManyMore>
              <FormattedMessage id="admin.dashboard.integrations.manyMore" />
            </ManyMore>
          </Flex>
            {showMore &&
              <Flex marginBottom={4} gap={theme.spacing.three}>
                {
                  !directorySyncEnabled && <InfoCard
                    title="admin.dashboard.integrations.card.dsync.title"
                    text="admin.dashboard.integrations.card.dsync.text" />
                }
                {
                  !singleSignOnEnabled && <InfoCard
                    isSSO
                    title="admin.dashboard.integrations.card.sso.title"
                    text="admin.dashboard.integrations.card.sso.text" />
                }
              </Flex>
            }
          <Flex justifyContent="space-between" alignItems="flex-end">
            <Button tertiary onClick={this.handleShowMore}>
              <Spacing marginRight={0.5}>
                <FormattedMessage id={buttonText} />
              </Spacing>
              <Icon icon={icon} extraLarge />
            </Button>
          </Flex>
        </>}
      </RelativeCard>
    )
  }
}

export default Integrations
